import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { LenderService } from '/src/api';
import { Lender, TLenderId } from '/src/lib/models';
import { MenuItemType, TListLendersRequest, UserRole } from '/src/lib/types';

export interface LenderStoreProps {
  /**
   * Shortcut to the User store
   */
  userStore: UserStore;
  /**
   * List of Lenders
   */
  lenders?: Lender[];
  /**
   * Currently selected Lender
   */
  currentLender?: Lender;
}

export class LenderStore extends BaseStore implements LenderStoreProps {
  private didInitMenu = false;
  userStore: UserStore;

  lenders?: Lender[];
  currentLender?: Lender;

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.userStore = rootStore.userStore;

    makeObservable(this, {
      lenders: observable,
      currentLender: observable,
    });
  }

  /**
   * Adds Lender nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Investors',
      'investors',
      this.rootStore.userStore.isLender ? MenuItemType.Item : MenuItemType.Dropdown,
      '/investors(/create)?',
      false,
      [UserRole.Admin, UserRole.Investor],
      6
    );
    if (!this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'investors', 'List Investors', 'investors');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'investors',
        'New Investor',
        'companies/create',
        'companies/create',
        false,
        [UserRole.Admin]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Lenders responses.
   *
   * @param companies - An array of Lenders to format
   * @returns The formatted Lenders
   */
  formatLenderList = (lenders: Lender[]) => {
    return lenders.map((lender) => {
      // Calculate
      if (lender.participations) {
        lender.loansParticipatedIn = lender.participations.length;
        lender.currentBalance = 0;
        for (const participation of lender.participations) {
          lender.currentBalance += participation.relativeBalance;
        }
      }

      // Return
      return lender;
    });
  };

  /**
   * Format Get Lender responses.
   *
   * @param lender - The Lender to format
   * @returns The formatted Lender
   */
  formatLender = (lender: Lender) => {
    // Remap
    lender.feesCollected = lender.feesPaid;
    lender.interestCollected = lender.interestPaid;
    lender.socketFees = lender.totalSocketFees;

    lender.totalRevenue = (lender.feesCollected as number) + (lender.interestCollected as number);
    lender.netRevenue = (lender.totalRevenue as number) - (lender.socketFees as number);

    // Calculate Account activity balances
    let balance = 0;

    // Add up line items
    lender.account.activity = lender.account.activity.map((activity) => ({
      ...activity,
      balance: (balance += activity.amount),
    }));

    // Return
    return lender;
  };

  /**** Lender CRUD ****/

  /**
   * Fetches a list of Lenders.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Lenders
   */
  query = async (query?: TListLendersRequest) => {
    const response = await LenderService.query(query);

    runInAction(() => (this.lenders = this.formatLenderList(response ?? [])));

    return this.lenders;
  };

  /**
   * Fetches a single Lender and sets it as the current Lender.
   *
   * @param lenderId - ID of the Lender to fetch
   * @returns A Lender object
   */
  get = async (lenderId: TLenderId) => {
    const response = await LenderService.get(lenderId);

    runInAction(() => {
      this.currentLender = response ? this.formatLender(response) : undefined;
    });

    return this.currentLender;
  };

  /**
   * Deactivates a Lender. This is equivalent to a soft delete.
   *
   * @param lenderId - ID of the Lender to deactivate
   * @returns The deactivated Lender
   */
  deactivate = async (lenderId: TLenderId) => {
    const response = await LenderService.deactivate(lenderId);

    return response;
  };
}
